import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const CelebrityLightbox = ({ celebrity, onClose }) => {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const photos = celebrity.photos || [];
  const total = photos.length;

  useEffect(() => { setIndex(0); }, [celebrity]);

  const goPrev = useCallback(() => setIndex(i => (i - 1 + total) % total), [total]);
  const goNext = useCallback(() => setIndex(i => (i + 1) % total), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext]);

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta > 0) goPrev(); else goNext();
    }
    touchStartX.current = null;
  };

  if (total === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 shrink-0">
        <div className="min-w-0">
          <p className="text-white text-sm font-medium truncate">{celebrity.name}</p>
          {total > 1 && <p className="text-white/50 text-xs">{index + 1} / {total}</p>}
        </div>
        <button
          onClick={onClose}
          className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors shrink-0"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Image */}
      <div className="flex-1 relative flex items-center justify-center px-3 sm:px-16 min-h-0">
        {total > 1 && (
          <button
            onClick={goPrev}
            className="absolute left-2 sm:left-4 flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        )}
        <img
          src={photos[index]?.image}
          alt={`${celebrity.name} ${index + 1}`}
          className="max-w-full max-h-full object-contain select-none"
        />
        {total > 1 && (
          <button
            onClick={goNext}
            className="absolute right-2 sm:right-4 flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
            aria-label="Next photo"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {total > 1 && (
        <div className="flex items-center justify-center gap-2 px-4 py-4 overflow-x-auto shrink-0">
          {photos.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setIndex(i)}
              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden border-2 shrink-0 transition-colors ${
                i === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-90'
              }`}
            >
              <img src={p.image} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CelebrityLightbox;
